import { api } from "@/lib/apiClient";
import { API_BASE } from "@/lib/apiClient";
import { guestTabletPublicFetch } from "@/lib/guestTabletPublicFetch";
import { getOrCreateInfoScreenDeviceToken } from "@/lib/infoScreenDeviceStorage";
import { INFO_SCREEN_DEVICE_HEADER } from "@/lib/infoScreenApi";
import { tenantSlug } from "@/lib/tenantSlug";
import type {
  EventbriteTestResult,
  InfoScreenEventCatalogItem,
  InfoScreenEventsConfig,
  InfoScreenEventsPayload,
  InfoScreenManualEvent,
  InfoScreenSyncResult,
} from "@/types/infoScreenEvents";

const TENANT_HEADER = "X-Tenant-Slug";

function publicHeaders(): HeadersInit {
  const token = getOrCreateInfoScreenDeviceToken();
  return {
    [TENANT_HEADER]: tenantSlug(),
    Accept: "application/json",
    [INFO_SCREEN_DEVICE_HEADER]: token,
  };
}

function buildPublicUrl(path: string): string {
  if (/^https?:\/\//i.test(path)) return path;
  if (!API_BASE) return path;
  return `${API_BASE}${path.startsWith("/") ? "" : "/"}${path}`;
}

export async function fetchInfoScreenEvents(
  displayCode: string,
): Promise<InfoScreenEventsPayload> {
  const path = `/api/v1/public/info-screen/events?display_code=${encodeURIComponent(displayCode)}`;
  const res = await guestTabletPublicFetch(buildPublicUrl, publicHeaders, path);
  if (!res.ok) {
    return {
      enabled: false,
      city_label: "Frankfurt am Main",
      events: [],
      rotation_seconds: 10,
      modules: [],
    };
  }
  return (await res.json()) as InfoScreenEventsPayload;
}

export async function fetchInfoScreenEventsConfig(): Promise<InfoScreenEventsConfig> {
  return api.get<InfoScreenEventsConfig>("/api/v1/info-screen/events/config");
}

export async function updateInfoScreenEventsConfig(body: {
  events?: Record<string, unknown>;
  modules?: InfoScreenEventsConfig["modules"];
}): Promise<InfoScreenEventsConfig> {
  return api.patch<InfoScreenEventsConfig>("/api/v1/info-screen/events/config", body);
}

export async function updateInfoScreenEventsSecrets(body: {
  eventbrite_token?: string | null;
  eventbrite_organization_id?: string | null;
  ticketmaster_api_key?: string | null;
}): Promise<InfoScreenEventsConfig> {
  return api.patch<InfoScreenEventsConfig>("/api/v1/info-screen/events/secrets", body);
}

export async function fetchManualEvents(): Promise<InfoScreenManualEvent[]> {
  return api.get<InfoScreenManualEvent[]>("/api/v1/info-screen/events/manual");
}

export async function fetchInfoScreenEventsCatalog(): Promise<InfoScreenEventCatalogItem[]> {
  return api.get<InfoScreenEventCatalogItem[]>("/api/v1/info-screen/events/catalog");
}

export async function createManualEvent(
  body: Omit<InfoScreenManualEvent, "id" | "archived_at" | "created_at" | "updated_at">,
): Promise<InfoScreenManualEvent> {
  return api.post<InfoScreenManualEvent>("/api/v1/info-screen/events/manual", body);
}

export async function updateManualEvent(
  id: string,
  body: Partial<InfoScreenManualEvent>,
): Promise<InfoScreenManualEvent> {
  return api.put<InfoScreenManualEvent>(`/api/v1/info-screen/events/manual/${id}`, body);
}

export async function archiveManualEvent(id: string): Promise<InfoScreenManualEvent> {
  return api.patch<InfoScreenManualEvent>(
    `/api/v1/info-screen/events/manual/${id}/archive`,
    {},
  );
}

export async function deleteManualEvent(id: string): Promise<void> {
  return api.del(`/api/v1/info-screen/events/manual/${id}`);
}

export async function syncInfoScreenEvents(
  providers?: string[],
): Promise<InfoScreenSyncResult> {
  return api.post<InfoScreenSyncResult>("/api/v1/info-screen/events/sync", {
    providers: providers ?? null,
  });
}

export async function testEventbriteConnection(): Promise<EventbriteTestResult> {
  return api.post<EventbriteTestResult>(
    "/api/v1/info-screen/events/providers/eventbrite/test",
    {},
  );
}
